import Bind from "lodash-decorators/bind";
import React, {
    ChangeEvent,
    ComponentClass,
    ComponentType,
    FocusEvent,
    FormEvent,
    PureComponent,
} from "react";
import { forwardRef, WithForwardedRef } from "../utils/ref";

type Validatable = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export interface ValidationRequiredProps {
    onChange?(e: ChangeEvent<any>): void;
    onBlur?(e: FocusEvent<any>): void;
    onInvalid?(e: FormEvent<any>): void;
}

type DefaultValidityMessages = {
    [K in Exclude<keyof ValidityState, "valid">]?: string;
};

export type ValidityMessages = DefaultValidityMessages & {
    [key: string]: string;
};

export interface WithValidationProps {
    uiReportOnBlur?: boolean;
    uiUseDefaultReporting?: boolean;
    uiValidityMessages?:
        | ValidityMessages
        | ((val: string, elem: HTMLElement) => ValidityMessages);
    uiCustomValidator?(val: string, elem: HTMLElement): { [key: string]: boolean };
    uiCustomAsyncValidator?(
        val: string,
        elem: HTMLElement
    ): Promise<{ [key: string]: boolean } | undefined>;
    uiOnValid?(elem: HTMLElement): void;
    uiOnInvalid?(msg: string, elem: HTMLElement, valid?: any): void;
    uiOnStartAsyncValidation?(elem: HTMLElement): void;
    uiOnFinishAsyncValidation?(valid: boolean, msg: string | undefined, elem: HTMLElement): void;
    name?: string;
    value?: string | number;
    isValid?: boolean;
    id?: string;
    required?: boolean;
}

function WithValidation<T extends ValidationRequiredProps>(
    Component: ComponentType<T>
): React.FC<T & WithValidationProps> {
    class WithValidationComponent extends PureComponent<
        WithValidationProps & ValidationRequiredProps
    > {
        public static displayName = `WithValidation(${
            Component.displayName || Component.name || "WrappedComponent"
        })`;
        public static defaultProps: Partial<WithValidationProps & ValidationRequiredProps> = {
            uiReportOnBlur: true,
        };

        public componentDidUpdate(newProps: Partial<WithValidationProps>): void {
            if (newProps.name === "billingType") {
                const elem = document.getElementById("billingType-selectList");
                if (elem && this.props.uiOnValid && newProps.value) {
                    this.props.uiOnValid(elem);
                }
            }
            if (this.props.name === "selectedSearchNameOwner") {
                const elem = document.getElementById("itemCategory");
                if (elem && this.props.uiOnValid && this.props.value) {
                    this.props.uiOnValid(elem);
                }
            }
            if (newProps.name === "displayName") {
                const elem = document.getElementById("displayNameOwner");
                if (elem && this.props.uiOnValid && newProps.value !== this.props.value) {
                    this.props.uiOnValid(elem);
                }
            }
            if (
                this.props.name &&
                this.props.id &&
                [
                    "Markup Cost Trade Input",
                    "Discount MSRP Trade Input",
                    "Markup Cost Retail Input",
                    "Discount MSRP Retail Input",
                    "Rate Retail Input",
                    "Rate Trade Input",
                    "Item Cost",
                    "Item MSRP",
                    "Shipping Cost Input",
                    "Handling Fee MarkUp Product Input",
                    "Handling Fee MarkUp Shipping Cost Input",
                    "Handling Fee Rate Input",
                    "Shipping Cost Input",
                    "Cost Plus Retail Input",
                    "Cost Plus Trade Input",
                    "handlingFee",
                ].includes(this.props.name)
            ) {
                const elem = document.getElementById(this.props.id);
                if (elem && this.props.uiOnValid && !this.props.required) {
                    this.props.uiOnValid(elem);
                }
            }
        }

        public render(): JSX.Element {
            const {
                uiCustomAsyncValidator,
                uiCustomValidator,
                uiOnFinishAsyncValidation,
                uiOnInvalid,
                forwardedRef,
                uiOnStartAsyncValidation,
                uiOnValid,
                uiReportOnBlur,
                uiValidityMessages,
                uiUseDefaultReporting,
                ...other
            } = this.props as WithForwardedRef<WithValidationProps & ValidationRequiredProps>;

            const Comp = Component as unknown as ComponentClass<ValidationRequiredProps>;
            return (
                <Comp
                    {...other}
                    onChange={this.onChange}
                    onBlur={this.onBlur}
                    onInvalid={this.onInvalid}
                    ref={forwardedRef}
                />
            );
        }

        @Bind()
        protected onChange(e: React.ChangeEvent<Validatable>): void {
            const { onChange, uiOnValid, uiValidityMessages, uiCustomValidator } = this.props;
            const elem = e.currentTarget;
            const val = elem.value;
            const defaultValidity = elem.validity;
            const finalValidity = {
                badInput: defaultValidity.badInput,
                patternMismatch: defaultValidity.patternMismatch,
                rangeOverflow: defaultValidity.rangeOverflow,
                rangeUnderflow: defaultValidity.rangeUnderflow,
                stepMismatch: defaultValidity.stepMismatch,
                tooLong: defaultValidity.tooLong,
                tooShort: defaultValidity.tooShort,
                typeMismatch: defaultValidity.typeMismatch,
                valueMissing: defaultValidity.valueMissing,
            };
            const defaultValidatorsValid = Object.entries(finalValidity).every(
                ([, invalid]) => !invalid
            );
            const customValidity =
                defaultValidatorsValid && uiCustomValidator
                    ? uiCustomValidator(val, elem)
                    : {};
            Object.assign(finalValidity, customValidity);
            const valid = Object.entries(finalValidity).every(([, invalid]) => !invalid);

            if (valid) {
                elem.setCustomValidity("");
                if (uiOnValid) {
                    uiOnValid(elem);
                }
            } else if (uiValidityMessages) {
                const messages =
                    typeof uiValidityMessages === "function"
                        ? uiValidityMessages(val, elem)
                        : uiValidityMessages;
                for (const key of Object.keys(finalValidity)) {
                    if (finalValidity[key]) {
                        if (messages[key]) {
                            elem.setCustomValidity(messages[key]);
                        } else {
                            elem.setCustomValidity("");
                        }
                        break;
                    }
                }
            }
            if (onChange) {
                onChange(e);
            }
        }

        @Bind()
        protected async onBlur(e: React.FocusEvent<Validatable>): Promise<void> {
            const {
                onBlur,
                uiReportOnBlur,
                uiCustomAsyncValidator,
                uiValidityMessages,
                uiOnStartAsyncValidation,
                uiOnFinishAsyncValidation,
            } = this.props;
            e.persist();
            const currentTarget = e.currentTarget;
            let valid = currentTarget.validity.valid;
            const val = currentTarget.value;
            if (valid && uiCustomAsyncValidator) {
                if (uiOnStartAsyncValidation) {
                    uiOnStartAsyncValidation(currentTarget);
                }
                const res = await uiCustomAsyncValidator(val, currentTarget);
                let message: string | undefined;
                if (res && typeof res === "object") {
                    const asyncNoValid = Object.entries(res).find(([, nonValid]) => nonValid);
                    if (asyncNoValid) {
                        valid = false;
                        const messages =
                            typeof uiValidityMessages === "function"
                                ? uiValidityMessages(val, currentTarget)
                                : uiValidityMessages;
                        message =
                            messages && messages[asyncNoValid[0]]
                                ? messages[asyncNoValid[0]]
                                : "Validation Failed";
                        currentTarget.setCustomValidity(message);
                    }
                }
                if (uiOnFinishAsyncValidation) {
                    uiOnFinishAsyncValidation(valid, message, currentTarget);
                }
            }
            if (uiReportOnBlur) {
                currentTarget.reportValidity();
            }
            if (onBlur) {
                await onBlur(e);
            }
        }

        @Bind()
        protected onInvalid(e: React.FormEvent<Validatable>): void {
            const { onInvalid, uiUseDefaultReporting, uiOnInvalid } = this.props;
            if (onInvalid) {
                onInvalid(e);
            }
            if (!uiUseDefaultReporting) {
                e.preventDefault();
            }
            if (uiOnInvalid) {
                if (
                    e.currentTarget.name !== "billingType" &&
                    !e.currentTarget.validationMessage.includes("The two nearest valid values are")
                ) {
                    uiOnInvalid(e.currentTarget.validationMessage, e.currentTarget);
                } else if (e.currentTarget.name === "billingType" && e.currentTarget.value === "") {
                    uiOnInvalid("Please select billing type for client.", e.currentTarget);
                }
            }
        }
    }

    return forwardRef(WithValidationComponent as any) as any;
}

export default WithValidation;
